/**
 * Resolve how many days from today an opportunity is due.
 *
 * Uses `endDate` (falling back to the legacy `dueDate` alias).
 * Returns a positive number for future due dates, 0 for today, negative when overdue,
 * and `null` when no due date can be resolved.
 */
export const getDueInDays = (objective: {
    endDate?: string
    dueDate?: string
}): number | null => {
    let due: Date | null = null

    const raw = (objective.endDate ?? objective.dueDate)?.trim()
    if (raw) {
        const parsed = new Date(`${raw}T12:00:00`)
        if (!Number.isNaN(parsed.getTime())) {
            due = parsed
        }
    }

    if (!due) return null

    const today = new Date()
    today.setHours(12, 0, 0, 0)
    const diffMs = due.getTime() - today.getTime()
    return Math.round(diffMs / 86_400_000)
}

/**
 * Whether an opportunity is still inside its validity window.
 *
 * The backend is the source of truth: it derives `isExpired` from the persisted
 * `endDate` with the due day inclusive, so the flag wins whenever it is present.
 * The date fallback exists only for payloads that predate the flag, and it
 * deliberately does NOT reconstruct the window from `daysOfValid` — `endDate` is
 * the single source of the window, so rebuilding it from a duration can disagree
 * with what the backend considers expired.
 *
 * An opportunity with no resolvable window counts as inactive: aggregations would
 * otherwise silently include something whose validity nobody can determine.
 */
export const isOpportunityActive = (objective: {
    isExpired?: boolean
    endDate?: string
    dueDate?: string
}): boolean => {
    if (typeof objective?.isExpired === "boolean") return !objective.isExpired

    const dueInDays = getDueInDays(objective ?? {})
    return dueInDays !== null && dueInDays >= 0
}
